// settings.ts — Patch 015.14 (Settings / Integrations page).
//
// Backend: workers/core-os/src/routes/settings.ts (org_settings migration
// 0068 + per-project cfg via project_facts 'cfg:' namespace).

import { request, BASE } from './request'

const pid = (id: string): string => encodeURIComponent(id)

export interface IntegrationsStatus {
  ok: true
  gmail: {
    connected: boolean
    account_email?: string | null
    connected_at?: string
    last_refreshed_at?: string | null
    token_expires_at?: string | null
    scope?: string | null
  }
  qbo: { connected: boolean; note: string }
}

export interface OrgSetting {
  id: string
  setting_key: string
  setting_value: string
  setting_type: 'text' | 'number' | 'boolean' | 'json'
  notes: string | null
  created_at: string
  updated_at: string | null
}

export interface ProjectSettings {
  ok: true
  config: Record<string, string>
  allowed_keys: string[]
}

// Wave-7 D9 — read-only AI-call telemetry, aggregated per triggering surface.
export interface AiCallSurfaceRow {
  surface: string
  calls: number
  models: string | null
  input_tokens: number
  output_tokens: number
  avg_latency_ms: number
}

export interface AiCallTelemetryResponse {
  ok: true
  by_surface: AiCallSurfaceRow[]
  total_calls: number
}

export const settingsApi = {
  integrations: () =>
    request<IntegrationsStatus>('/api/settings/integrations'),
  orgSettings: () =>
    request<{ ok: true; settings: OrgSetting[] }>('/api/settings/org'),
  putOrgSetting: (
    key: string,
    body: { setting_value: string; setting_type?: string; notes?: string },
  ) =>
    request<{ ok: true; id: string; key: string; created: boolean }>(
      `/api/settings/org/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  projectSettings: (projectId: string) =>
    request<ProjectSettings>(`/api/projects/${pid(projectId)}/settings`),
  putProjectSettings: (projectId: string, config: Record<string, string>) =>
    request<{ ok: true; written: string[] }>(
      `/api/projects/${pid(projectId)}/settings`,
      { method: 'PUT', body: JSON.stringify({ config }) },
    ),
  // The tax-export CSV is a file download — surface the URL, not a fetch.
  taxExportUrl: (year: number): string =>
    `${BASE}/api/settings/tax-export.csv?year=${year}`,
  // Wave-7 D9 — read-only AI-call telemetry for the Settings/Ops surface.
  aiCallTelemetry: () =>
    request<AiCallTelemetryResponse>('/api/settings/ai-call-telemetry'),
}
